import { ApolloClient, ApolloLink, InMemoryCache, split } from '@apollo/client';
import { BatchHttpLink } from '@apollo/client/link/batch-http';
import { setContext } from '@apollo/client/link/context';
import { onError } from '@apollo/client/link/error';
import { RetryLink } from '@apollo/client/link/retry';
import { GraphQLWsLink } from '@apollo/client/link/subscriptions';
import { clearToken } from '@app//helpers/auth';
import { configs } from '@app/configs';
import { captureException } from '@sentry/react';
import { getMainDefinition } from 'apollo-utilities';
import { persistCache } from 'apollo3-cache-persist';
import { createClient } from 'graphql-ws';
import { mergeBidsByTimestamp } from './bidCacheMerge';
import { SystemEnum } from './codegen/graphql';
const cache = new InMemoryCache({
    typePolicies: {
        Query: {
            fields: {
                companyOrders: {
                    keyArgs: ['query', 'paginate', 'sortBy'],
                },
                costprofilesAdditionalCharges: {
                    merge: false,
                },
                notifications: {
                    keyArgs: false,
                },
                carrierGroups: {
                    merge(_, incoming) {
                        return incoming;
                    },
                },
            },
        },
        Address: {
            merge: true,
        },
        CostprofilesVehicle: {
            fields: {
                companyOrders: {
                    merge: false,
                },
            },
        },
        Leg: {
            merge: true,
        },
        LegRoute: {
            merge: true,
        },
        QuoteRouting: {
            merge: false,
            fields: {
                routes: {
                    merge: false,
                },
            },
        },
        Order: {
            merge: true,
            fields: {
                files: {
                    merge: false,
                },
            },
        },
        Load: {
            merge: true,
        },
        Route: {
            merge: true,
        },
        Bid: {
            keyFields: false,
        },
        CompanyOrder: {
            merge: true,
            fields: {
                bids: {
                    merge: mergeBidsByTimestamp,
                },
            },
        },
        OfferDetails: {
            merge: true,
        },
        OffersRequestedDetails: {
            fields: {
                bids: {
                    merge: mergeBidsByTimestamp,
                },
            },
        },
        Toll: {
            merge: true,
        },
        Driver: {
            fields: {
                driverApp: {
                    merge: true,
                },
            },
        },
        ChangeRequestStop: {
            // This is needed to prevent incorrect caching of stops in change requests
            keyFields: () => false,
        },
        ShipperConnection: {
            fields: {
                groups: {
                    merge(_, incoming) {
                        return incoming;
                    },
                },
            },
        },
    },
});
persistCache({
    cache,
    storage: window.sessionStorage,
});
const authenticationErrors = [
    'session-deactivated',
    'password-changed',
    'unauthenticated',
    'auth-failed',
];
// Used in both ws and http link
const graphQLErrorsLink = onError(({ graphQLErrors }) => {
    var _a;
    if (graphQLErrors) {
        for (const err of graphQLErrors) {
            const errorMessage = (_a = err.message) === null || _a === void 0 ? void 0 : _a.replace(/^Context creation failed: /, '');
            if (authenticationErrors.includes(errorMessage)) {
                clearToken();
                window.location.pathname = '/user/login';
                return;
            }
        }
    }
});
// Used only in http link,
// Network errors in subscriptions
// is handled via retries
const networkErrorLink = onError(({ networkError, graphQLErrors }) => {
    if (networkError && !(graphQLErrors === null || graphQLErrors === void 0 ? void 0 : graphQLErrors.length)) {
        captureException(networkError, { tags: { api: 'graphql' } });
    }
});
const authContext = setContext(() => {
    const token = localStorage.getItem('token');
    return {
        headers: {
            authorization: token || '',
        },
    };
});
const splitLinks = ({ query }) => {
    // split based on operation type
    const { kind, operation } = getMainDefinition(query);
    return kind === 'OperationDefinition' && operation === 'subscription';
};
const isOperationTypeMatching = (query, operationType) => {
    const { kind, operation } = getMainDefinition(query);
    return kind === 'OperationDefinition' && operation === operationType;
};
const graphqlWslink = new GraphQLWsLink(createClient({
    url: configs.SUBSCRIPTIONS_URL,
    lazy: true,
    retryAttempts: Infinity,
    shouldRetry: () => true,
    connectionParams: () => ({
        authorization: localStorage.getItem('token') || '',
    }),
}));
const retryLink = new RetryLink({
    attempts: {
        retryIf: (error, operation) => {
            const isNetworkError = !error.statusCode || error.statusCode === 504;
            return isNetworkError && isOperationTypeMatching(operation.query, 'query'); // Do not retry mutations.
        },
    },
});
const wsLink = ApolloLink.from([
    graphQLErrorsLink,
    graphqlWslink,
]);
const appVersionLink = setContext((_, { headers }) => {
    return {
        headers: Object.assign(Object.assign({}, headers), { 'x-app-version': `${SystemEnum.shipper}-${RELEASE}` }),
    };
});
const httpLink = ApolloLink.from([
    graphQLErrorsLink,
    networkErrorLink,
    retryLink,
    authContext,
    appVersionLink,
    new BatchHttpLink({
        uri: configs.API_URL,
    }),
]);
const link = split(splitLinks, wsLink, httpLink);
const client = new ApolloClient({
    link,
    cache,
});
export default client;
